import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import { BADGE_TYPES } from './badgeTypes';
import { normalizeAddress } from './addressUtils';

// Re-export BADGE_TYPES for convenience
export { BADGE_TYPES };

/**
 * Get all badges for a specific address
 *
 * Uses chain-agnostic address normalization
 *
 * @param {string} address - The address to get badges for
 * @param {string} chain - The blockchain chain (defaults to 'ethereum', kept for compatibility)
 * @returns {Promise<Array>} Array of badge objects
 */
export const getUserBadges = async (address, chain = 'ethereum') => {
  if (!address) return [];

  try {
    // Normalize address (chain-agnostic)
    // EVM: lowercase hex (0xabc...)
    // Non-EVM: preserve case (1ABC...)
    const uid = normalizeAddress(address);
    const addressRef = doc(db, 'addresses', uid);
    const addressDoc = await getDoc(addressRef);

    if (addressDoc.exists()) {
      const data = addressDoc.data();

      // Return badges array if it exists, otherwise empty array
      const badges = data.badges || [];

      // Filter out invalid badges and add display info
      return badges
        .filter(badge => badge && badge.type && BADGE_TYPES[badge.type])
        .map(badge => ({
          ...badge,
          ...BADGE_TYPES[badge.type]
        }));
    }

    return [];
  } catch (error) {
    console.error('Error getting user badges:', error);
    return [];
  }
};

/**
 * Get badges for multiple addresses (batch operation)
 * @param {Array<string>} addresses - Array of addresses to get badges for
 * @param {string} chain - The blockchain chain (defaults to 'ethereum')
 * @returns {Promise<Object>} Map of address -> badges array
 */
export const getBatchUserBadges = async (addresses, chain = 'ethereum') => {
  if (!addresses || addresses.length === 0) return {};

  try {
    const badgesMap = {};

    // Fetch all badges in parallel
    await Promise.all(
      addresses.map(async (address) => {
        const normalizedAddress = normalizeAddress(address);
        const badges = await getUserBadges(normalizedAddress, chain);
        if (badges.length > 0) {
          badgesMap[normalizedAddress] = badges;
        }
      })
    );

    return badgesMap;
  } catch (error) {
    console.error('Error getting batch user badges:', error);
    return {};
  }
};
